import { useEffect, useMemo, useRef, useState } from 'react';

// Hooks
import { useMutation } from '@tanstack/react-query';
import { useInterval } from '@intigral/hooks';
import useEventTracking from '../../../hooks/useEventTracking';
import { fetchEpgDataByDay, fetchEpgDataByChannelId } from '../../../hooks/useEpgData';
import useChannelSearch from '../../../hooks/useChannelSearch';
import useBookmark from '../../../hooks/bookmarks/useBookmark';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { usePPVEvents } from '../../../hooks/usePPVEvents';

// Helpers
import channelsUtil from '../../../helpers/channelsUtil';
import f2 from '../../../helpers/formatter2';
import { getUserInfo } from '../../../utils/user';

// Interfaces
import { SearchParameters, UserChannels } from '../types/iEpgGudeContainer';

// Libraries
import type { Program } from 'planby';

// Translations
import { useUserReminders } from '../../../store/reducers/reminderSlice';
import useProgramRemainingTime from '../../../hooks/useProgramRemainingTime';
import usePPVInfo from '../../../hooks/usePPVInfo';
import { DEFAULT_GENRE_SELECTED, PROGRAM_REFRESH } from '../constants';
import { useEpgHandler } from './useEpgHandler';
import useLanguage from '../../../hooks/useLanguage';
import { useChannelFavorites } from '../../../hooks/useChannelFavorites';
import { isChannelLocked } from '../utils/isChannelLocked';
import { getChannelFutureDate, getChannelPreviousDate } from '../utils/EpgUtils';
import moment from 'moment';
import type { UserInfo } from '@intigral/domain';
import { userAccessControl } from '../../../utils/userAccessControl';

export const useEpgGuide = (searchParameters: SearchParameters, userChannels: UserChannels) => {
    // Destructure incoming search parameters
    const { channelId, id, listingId, genre, startDate } = searchParameters;
    const rewindDay = channelsUtil.getFormattedTime(Number(startDate), 'YYYY-MM-DD');
    // Hooks
    const search = useChannelSearch();
    const { setSelected: setBookmarkItem } = useBookmark();
    const { ppvSubscribedList } = usePPVInfo();
    const { sendDestroyEvent } = useEventTracking();
    const sendDestroyEventRef = useRef(sendDestroyEvent);
    sendDestroyEventRef.current = sendDestroyEvent;
    const { updateRemainingTime, resetRemainingTime, currentProgramRemainingTime, currentProgramDuration } =
        useProgramRemainingTime();
    const userInfo = getUserInfo() as UserInfo;
    const { data: reminderData } = useUserReminders({ detailed: false }, userInfo);
    const {
        isFavorite,
        setIsFavorite,
        favoriteChanels: { data: favorites },
    } = useChannelFavorites();

    // Using React Query instead of Redux for configuration
    const { channelImage = {}, freeVsPaid = {}, operators = {} } = useConfigurationSections();
    const channelImageId = (channelImage as any)?.id || 'colour_264x26';
    const freeTierGuid = (freeVsPaid as any)?.freeTierGuid;
    //  FIXED: Using React Query-style hook instead of Redux generalConfiguration
    const { data: ppvEventConfig } = usePPVEvents();

    // Helpers & Utilities
    const language = useLanguage();
    const epgDates = channelsUtil.getEPGDays();
    const todayDate = channelsUtil.getFormattedTime(Date.now(), 'YYYY-MM-DD');

    // State
    const [selectedDate, setSelectedDate] = useState(todayDate);
    const [selectedCategory, setSelectedCategory] = useState(DEFAULT_GENRE_SELECTED);
    const [listedChannels, setListedChannels] = useState(null);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [streamingUrl, setStreamingUrl] = useState(null);
    const [isStartOver, setIsStartOver] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [isFutureProgram, setIsFutureProgram] = useState(false);
    const [isRewindProgram, setIsRewindProgram] = useState(false);
    const [isLive, setIsLive] = useState(true);
    const [searchChannels, setSearchChannels] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [isShowEpgLock, setIsShowEpgLock] = useState(false);
    const [isSwitch, setIsSwitch] = useState(false);
    const [previousDates, setPreviousDates] = useState([]);
    const [futureDates, setFutureDates] = useState([]);
    const [fetchingProgramsForChannel, setFetchingProgramsForChannel] = useState<any | null>(null);
    const selectedProgramKey = selectedProgram?.id ?? selectedProgram?.listingId ?? null;

    useEffect(() => {
        if (startDate) {
            const formatedDate = moment(Number(startDate)).format('YYYY-MM-DD');
            setSelectedDate(formatedDate);
        }
    }, []);

    const mutation = useMutation({
        mutationFn: async ({
            day,
            channels,
            rewindDate,
        }: {
            day: string;
            rewindDate: string;
            channels: UserChannels;
        }) => {
            return fetchEpgDataByDay(day, rewindDate, channels as any);
        },
    });

    const getProgramMutation = useMutation({
        mutationFn: async ({
            day,
            channels,
            rewindDate,
            channelId,
        }: {
            day: string;
            rewindDate: string;
            channels: UserChannels;
            channelId: string;
        }) => {
            return fetchEpgDataByChannelId(day, rewindDate, channels as any, channelId);
        },
    });

    useEffect(() => {
        const channelId = selectedProgram?.channel?.channelID;
        if (!channelId || !favorites) return;

        setIsFavorite(favorites.includes(channelId));
    }, [selectedProgram?.channel?.channelID, favorites]);

    useEffect(() => {
        setStreamingUrl(null);
        mutation.mutate(
            { day: selectedDate, rewindDate: rewindDay, channels: userChannels },
            {
                onSuccess: (data) => {
                    setPrograms(data);
                },
            }
        );
    }, [selectedDate]);

    const fetchPreviousProgramOnScroll = (channelId: string) => {
        const currentDate = getChannelPreviousDate(channelId, selectedDate, previousDates);
        const d = new Date(currentDate);
        d.setDate(d.getDate() - 1);
        const preDate = d.toISOString().split('T')[0];

        setPreviousDates((prev) => {
            const exists = prev.find((p) => p.channelId === channelId);

            if (!exists) {
                return [...prev, { channelId, date: preDate }];
            }

            return prev.map((p) => (p.channelId === channelId ? { ...p, date: preDate } : p));
        });

        const channelData = userChannels.channels.find((c) => c.channelID === channelId);
        const rewindBufferHours = channelData?.rights?.rewindBuffer;

        const now = new Date();
        const pastDate = new Date(now.getTime() - rewindBufferHours * 60 * 60 * 1000);

        // Format as YYYY-MM-DD
        const formatDate = (date) => {
            return date.toISOString().split('T')[0];
        };

        const pastFomattedDate = formatDate(pastDate);
        if (preDate >= pastFomattedDate) {
            setFetchingProgramsForChannel({
                channelId,
                channelTitle: userChannels.channels.find((c) => c.channelID === channelId)?.channelTitle,
            });
            getProgramMutation.mutate(
                {
                    day: preDate,
                    rewindDate: rewindDay,
                    channels: userChannels,
                    channelId,
                },
                {
                    onSuccess: (data) => {
                        setPrograms((prevPrograms: any) => [...data, ...prevPrograms]);
                    },
                }
            );
        }
    };

    const fetchFutureProgramOnScroll = (channelId: string) => {
        const currentDate = getChannelFutureDate(channelId, selectedDate, futureDates);
        const d = new Date(currentDate);
        d.setDate(d.getDate() + 1);
        const nextDate = d.toISOString().split('T')[0];

        // update only this channel's future date
        setFutureDates((prev) => {
            const exists = prev.find((p) => p.channelId === channelId);

            if (!exists) {
                return [...prev, { channelId, date: nextDate }];
            }

            return prev.map((p) => (p.channelId === channelId ? { ...p, date: nextDate } : p));
        });

        // 🚫 Prevent going too far in future (optional safety)
        const today = new Date();
        const maxFuture = new Date();
        maxFuture.setDate(today.getDate() + 7); // example: allow 7 days ahead

        const formattedMaxFuture = maxFuture.toISOString().split('T')[0];

        if (nextDate <= formattedMaxFuture) {
            setFetchingProgramsForChannel({
                channelId,
                channelTitle: userChannels.channels.find((c) => c.channelID === channelId)?.channelTitle,
            });
            getProgramMutation.mutate(
                {
                    day: nextDate,
                    rewindDate: rewindDay,
                    channels: userChannels,
                    channelId,
                },
                {
                    onSuccess: (data) => {
                        // append to the END (future)
                        setPrograms((prevPrograms: any) => [...prevPrograms, ...data]);
                    },
                }
            );
        }
    };

    // Memos
    const genres = useMemo(() => {
        return channelsUtil.compileLiveTVGenresWithCounts(userChannels?.channels, favorites);
    }, [searchChannels, favorites]);

    const channels = useMemo(() => {
        let formattedChannels = f2.formattedChannelData(listedChannels || userChannels?.channels || [], channelImageId);

        formattedChannels = formattedChannels.map((channel) => {
            return {
                ...channel,
                locked: isChannelLocked(channel),
            };
        });

        // Filter out PPV channels that haven't been purchased
        const filteredChannels = formattedChannels
            .filter((channel) => {
                // If channel has PPV subscription
                const hasPPVSubscription = channel.subscriptions?.some((sub) =>
                    ppvSubscribedList.some((ppv) => ppv.subscriptionGuid === sub.subscriptionGUID)
                );

                // If channel has no PPV subscription, show it
                if (
                    !channel.subscriptions?.some((sub) =>
                        ppvSubscribedList.some((ppv) => ppv.subscriptionGuid === sub.subscriptionGUID)
                    )
                ) {
                    return true;
                }

                // If channel has PPV subscription, only show if user has purchased it
                return hasPPVSubscription;
            })
            .filter((channel) => userAccessControl.canAccessChannel(channel));

        return channelsUtil.getEpgFilteredChannels(filteredChannels, selectedDate);
    }, [listedChannels, userChannels?.channels, channelImageId, selectedDate, ppvSubscribedList]);

    useEffect(() => {
        if (!channels?.length) return;

        const d = new Date();
        const today = d.toISOString().split('T')[0];

        const tomorrow = new Date(selectedDate);
        // only set +1 if current date is today, because we are calling tommorrrow along with today
        if (selectedDate === today) {
            tomorrow.setDate(tomorrow.getDate() + 1);
        } else {
            tomorrow.setDate(tomorrow.getDate());
        }

        const formattedTomorrow = tomorrow.toISOString().split('T')[0];

        // Past (today as baseline)
        setPreviousDates(
            channels.map((c) => ({
                channelId: c.id,
                date: selectedDate,
            }))
        );

        // Future (start from tomorrow)
        setFutureDates(
            channels.map((c) => ({
                channelId: c.id,
                date: formattedTomorrow,
            }))
        );
    }, [channels, selectedDate]);

    // handler
    const {
        onCategoryChangeHandler,
        // onDateChangeHandler,
        onLockClickHandler,
        onFavoriteClickHandler,
        startOverPlayback,
        resetState,
        onProgramSelectHandler,
        onChannelSelectHandler,
        onChannelSearchSelectHandler,
        onGoLiveHandler,
        onRewindCompleted,
        playerReadyCallback,
        handlePPVPurchase,
    } = useEpgHandler({
        favorites,
        userChannels,
        freeTierGuid,
        channelImageId,
        DEFAULT_GENRE_SELECTED,
        programs,
        channels,
        ppvEventConfig,
        operators,
        selectedProgram,
        isStartOver,
        selectedDate,
        streamingUrl,
        isFutureProgram,
        setSelectedCategory,
        setListedChannels,
        setSelectedChannel,
        setSelectedDate,
        setStreamingUrl,
        setIsStartOver,
        setIsFutureProgram,
        setIsRewindProgram,
        setSelectedProgram,
        setIsShowEpgLock,
        setIsSwitch,
    });

    const sendBookmark = (selected) => {
        setBookmarkItem(selected);
    };

    // check for next program and auto select when the program starts
    const { start, stop } = useInterval(() => {
        const currentProgram = selectedProgram;
        const selectedChannelPrograms = programs?.filter(
            (program) => program?.channelUuid === currentProgram?.channelUuid
        );
        const index = channelsUtil.getCurrentProgramIndex(selectedChannelPrograms);
        const nextProgram = selectedChannelPrograms && selectedChannelPrograms[index];
        if (currentProgram?.id !== nextProgram?.id && nextProgram) {
            onProgramSelectHandler(nextProgram, nextProgram?.channel ?? selectedChannel);
        }
    }, PROGRAM_REFRESH);

    // Effects
    useEffect(() => {
        if (!isRewindProgram) {
            start();
            return () => {
                stop();
            };
        }
    }, [selectedProgram, isRewindProgram]);

    useEffect(() => {
        setSearchChannels(channels);
    }, [channels]);

    useEffect(() => {
        if (genre) onCategoryChangeHandler(genre);
    }, [genre]);

    useEffect(() => {
        if (selectedChannel) onChannelSelectHandler(selectedChannel);
    }, [selectedChannel]);

    /**
     * Handles the change of date selection.
     *
     * @param {string} date - The newly selected date in string format.
     */
    const onDateChangeHandler = (date: string) => {
        // Update the selected date state
        setSelectedDate(date);
    };

    useEffect(() => {
        const programTimeStatus = channelsUtil.isNow(selectedProgram, selectedDate);
        setIsLive(programTimeStatus);
        // to reset the remaining time when the program changes
        resetRemainingTime();
    }, [selectedProgram]);

    const usePrevious = (value) => {
        const prevValueRef = useRef(0);

        useEffect(() => {
            if (!value) {
                return;
            }
            prevValueRef.current = value;
        }, [value]);

        return { prev: prevValueRef.current, current: value };
    };

    const { prev: prevProgramsCount, current: currentProgramsCount } = usePrevious(programs?.length ?? 0);
    const { prev: prevSelectedDate, current: currentSelectedDate } = usePrevious(selectedDate);
    const isDateChanged = useRef(false);

    useEffect(() => {
        if (!programs.length || !channels.length) return;
        const getProgramById = (id: string | null | undefined) => (id ? programs?.find((p) => p.id === id) : undefined);

        const getProgramByListingId = (listingId: string | null | undefined) =>
            listingId ? programs?.find((p) => p.listingId === listingId) : undefined;

        const getFallbackProgram = (): Program | undefined => {
            const channel_id = channelId ?? selectedChannel?.channelID ?? channels?.[0]?.id;
            const channelPrograms = programs?.filter((p) => p.channelUuid === channel_id);
            const currentChannel: any = channels?.find((c) => c.id === channel_id);
            if (currentChannel) channelsUtil.setSelectedChannel({ ...currentChannel });
            let index;
            if (selectedDate < todayDate) {
                index = 0;
            } else {
                index = channelsUtil.getCurrentProgramIndex(channelPrograms);
            }
            return channelPrograms?.[index];
        };
        let programData: Program | undefined;
        if (listingId !== null) {
            programData = getProgramByListingId(listingId);
        } else {
            programData = getProgramById(id);
        }
        if (!programData) {
            programData = getFallbackProgram();
        }

        // Find the corresponding channel for the selected program
        // const selectedChannel = programData ? channels?.find((c) => c.id === programData.channelUuid) : undefined;

        const till = programData?.till ? moment(Number(programData.till)).format('YYYY-MM-DD') : 'N/A';
        if (prevSelectedDate !== currentSelectedDate) {
            isDateChanged.current = true;
        }

        // TODO: review and update this logic as it is very fragile and depends on the order of useEffects and how the data is loaded which can easily break with any change in the future
        if (
            // HACK to select live program of the first channel on initial channels/programs load...
            (currentProgramsCount > prevProgramsCount && prevProgramsCount === 0) ||
            // (prevProgramsCount !== currentProgramsCount &&
            // (prevProgramsCount > currentProgramsCount &&
            //     selectedDate !== todayDate &&
            //     fetchingProgramsForChannel?.channelId === programData?.channelUuid) ||
            // ... or when the date is changed and the program to be selected is in the selected channel
            (isDateChanged.current && till === currentSelectedDate)
        ) {
            onProgramSelectHandler({ ...programData, hideUpgradeBanner: true }, selectedChannel);
            isDateChanged.current = false;
        }
    }, [programs, selectedDate]);

    useEffect(() => {
        const program = selectedProgram;

        return () => {
            if (program) {
                sendDestroyEventRef.current(program);
            }
        };
    }, [selectedProgramKey]);

    const isPastProgram = useMemo(() => {
        return !isLive && !isFutureProgram;
    }, [isLive, isFutureProgram]);

    const onPlayerReadyCallback = (player: any) => {
        playerReadyCallback(player);
        // update the elapsed time when the player is ready
        updateRemainingTime(player, isPastProgram, selectedProgram);
    };

    return {
        streamingUrl,
        isStartOver,
        isFutureProgram,
        programs,
        channels,
        genres,
        selectedCategory,
        selectedDate,
        isFavorite,
        search,
        selectedProgram,
        selectedChannel,
        isRewindProgram,
        favorites,
        epgDates,
        searchChannels,
        language,
        reminderData,
        isShowEpgLock,
        mutation,
        currentProgramRemainingTime,
        currentProgramDuration,
        isSwitch,
        setSearchChannels,
        onPlayerReadyCallback,
        onCategoryChangeHandler,
        onDateChangeHandler,
        onFavoriteClickHandler,
        setSelectedCategory,
        handlePPVPurchase,
        onGoLiveHandler,
        onLockClickHandler,
        startOverPlayback,
        // resetState,
        onProgramSelectHandler,
        onChannelSelectHandler,
        onChannelSearchSelectHandler,
        onRewindCompleted,
        playerReadyCallback,
        sendBookmark,
        fetchPreviousProgramOnScroll,
        fetchFutureProgramOnScroll,
        getProgramMutation,
    };
};
